const VIEWBOX_WIDTH = 100;
const VIEWBOX_HEIGHT = 100;
const VIEWBOX_HEIGHT_HALF = 50;
const VIEWBOX_CENTER_X = 50;
const VIEWBOX_CENTER_Y = 50;

const MIN_PERCENTAGE = 0;
const MAX_PERCENTAGE = 100;
const SVGNS = 'http://www.w3.org/2000/svg';
const SIZE = {
    small: 'w5-circle-percentage--small',
    medium: 'w5-circle-percentage--medium',
    big: 'w5-circle-percentage--big',
};

const defaultProps = {
    strokeWidth: 6,
    className: '',
    text: '',
    background: false,
    backgroundPadding: 0,
    initialAnimation: false,
    counterClockwise: false,
    circleRatio: 1,
    classes: {
        root: 'w5-circle-percentage__root',
        trail: 'w5-circle-percentage__trail',
        path: 'w5-circle-percentage__path',
        text: 'w5-circle-percentage__text',
        background: 'w5-circle-percentage__background',
    },
    styles: {
        root: '',
        trail: '',
        path: '',
        text: '',
        background: '',
    },
};

const getPathDescription = (props) => {
    const { pathRadius, counterClockwise } = props;
    const radius = pathRadius;
    const rotation = counterClockwise ? 1 : 0;
    return (
        '\n      M ' +
        VIEWBOX_CENTER_X +
        ',' +
        VIEWBOX_CENTER_Y +
        '\n      m 0,-' +
        radius +
        '\n      a ' +
        radius +
        ',' +
        radius +
        ' ' +
        rotation +
        ' 1 1 0,' +
        2 * radius +
        '\n      a ' +
        radius +
        ',' +
        radius +
        ' ' +
        rotation +
        ' 1 1 0,-' +
        2 * radius +
        '\n    '
    );
};

const getDashStyle = (props) => {
    const { pathRadius, percentage, counterClockwise } = props;
    const diameter = Math.PI * 2 * pathRadius;
    const truncatedPercentage = Math.min(Math.max(percentage, MIN_PERCENTAGE), MAX_PERCENTAGE);
    const gapLength = (1 - truncatedPercentage / 100) * diameter;
    const finalGapLength = counterClockwise ? `-${gapLength}px` : `${gapLength}px`;
    return `stroke-dasharray: ${diameter}px, ${diameter}px; stroke-dashoffset: ${finalGapLength};`;
};

const createPath = (props) => {
    const { className, counterClockwise, pathRadius, percentage, strokeWidth, style } = props;

    const newPath = document.createElementNS(SVGNS, 'path');
    newPath.setAttributeNS(null, 'class', className);
    newPath.setAttributeNS(null, 'd', getPathDescription({ pathRadius, counterClockwise }));
    newPath.setAttributeNS(null, 'stroke-width', strokeWidth);
    newPath.setAttributeNS(null, 'fill-opacity', 0);
    newPath.setAttributeNS(
        null,
        'style',
        getDashStyle({ pathRadius, percentage, counterClockwise }) + style,
    );
    return newPath;
};

const getPathRadius = (finalProps) => {
    return VIEWBOX_HEIGHT_HALF - finalProps.strokeWidth / 2;
};

export default function CirclePercentage(value, params) {
    const {
        isAnimate = false,
        withText = true,
        size = 'medium',
        sizeNumber,
        interval = 10,
        override = {},
    } = params;
    const finalProps = {
        ...defaultProps,
        ...override,
    };

    const { classes, className, styles, counterClockwise, strokeWidth, circleRatio } = finalProps;

    const total = value || 0;
    const pathRadius = getPathRadius(finalProps);
    let percentage = isAnimate ? 0 : total;

    let text = isAnimate ? 0 : total;

    const container = document.createElement('div');
    container.classList.add('w5-circle-percentage__container');
    const content = document.createElement('div');
    content.classList.add('w5-circle-percentage__content');

    if (sizeNumber)
        content.setAttribute('style', `height: ${sizeNumber}px; width: ${sizeNumber}px`);
    else {
        const sizeClass = SIZE[size];
        content.classList.add(sizeClass);
    }

    const svg = document.createElementNS(SVGNS, 'svg');
    svg.setAttributeNS(null, 'class', `${classes.root} ${className}`);
    svg.setAttributeNS(null, 'style', styles.root);
    svg.setAttributeNS(null, 'viewBox', `0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`);

    const trail = createPath({
        className: classes.trail,
        counterClockwise,
        pathRadius,
        percentage: 100 * circleRatio,
        strokeWidth,
        style: styles.trail,
    });

    let pathClasses = [classes.path];

    let pathColorClass = 'circle--red';
    if (total >= 25 && total < 75) {
        pathColorClass = 'circle--yellow';
    } else if (total >= 75) {
        pathColorClass = 'circle--green';
    }
    pathClasses.push(pathColorClass);

    const pathCircle = createPath({
        className: pathClasses.join(' '),
        counterClockwise,
        pathRadius,
        percentage: percentage * circleRatio,
        strokeWidth,
        style: styles.path,
    });

    const textEl = document.createElementNS(SVGNS, 'text');
    textEl.setAttributeNS(null, 'class', classes.text);
    textEl.setAttributeNS(null, 'style', styles.text);
    textEl.setAttributeNS(null, 'x', `${VIEWBOX_CENTER_X}%`);
    textEl.setAttributeNS(null, 'y', `${VIEWBOX_CENTER_Y}%`);
    textEl.setAttributeNS(null, 'text-anchor', 'middle');
    textEl.setAttributeNS(null, 'dominant-baseline', 'middle');
    const textNode = document.createTextNode(`${text}%`);
    textEl.appendChild(textNode);

    if (isAnimate && value > 0) {
        const updateProgress = () => {
            percentage++;
            pathCircle.setAttributeNS(
                null,
                'style',
                getDashStyle({ pathRadius, percentage, counterClockwise }) + styles.path,
            );
            textNode.nodeValue = `${percentage}%`;

            if (percentage === total || percentage === 100) clearInterval(animate);
        };
        const animate = setInterval(updateProgress, interval);
    }

    svg.appendChild(trail);
    svg.appendChild(pathCircle);
    if (withText) svg.appendChild(textEl);
    content.appendChild(svg);
    container.appendChild(content);
    return container;
}
